import { apiService } from "src/services";
import type {
  IPersonaAvatarAsset,
  IPersonaMarketContextStatus,
  IPersonaProject,
  IResponse,
} from "src/pages/Studies/models/interfaces";
import type { ICreatePersonaProjectRequest } from "../models/interfaces";

/**
 * Fetch all persona projects.
 */
export const fetchPersonaProjects = async (): Promise<{
  projects: IPersonaProject[];
}> => {
  const res = await apiService.get<{ data: { projects: IPersonaProject[] } }>(
    "/persona/persona-projects"
  );
  return res.data;
};

/**
 * Fetch one persona project detail for setup/chat workflows.
 */
export const fetchPersonaProject = async (
  projectId: string
): Promise<IPersonaProject> => {
  const res = await apiService.get<{ data: IPersonaProject }>(
    `/persona/persona-projects/${projectId}`
  );
  return res.data;
};

/**
 * Fetch approved Persona avatar assets from the backend facade.
 */
export const fetchPersonaAvatarAssets = async (
  query?: string
): Promise<{ assets: IPersonaAvatarAsset[]; available?: boolean; reason?: string | null }> => {
  const params = query ? `?query=${encodeURIComponent(query)}` : "";
  const res = await apiService.get<{
    data: { assets: IPersonaAvatarAsset[]; available?: boolean; reason?: string | null };
  }>(`/persona/avatar-assets${params}`);
  return res.data;
};

/**
 * Create a new persona project.
 */
export const createPersonaProject = async (
  req: ICreatePersonaProjectRequest
): Promise<IPersonaProject> => {
  const res = await apiService.post<{ data: IPersonaProject }>("/persona/persona-projects", req);
  return res.data;
};

/**
 * Delete a persona project by ID.
 */
export const deletePersonaProjectApi = async (
  projectId: string
): Promise<{ ok: boolean }> => {
  const res = await apiService.delete<{ data: { ok: boolean } }>(
    `/persona/persona-projects/${projectId}`
  );
  return res.data;
};

/**
 * Fetch profile users for the Persona collaborator modal.
 */
export const getPersonaProjectAllUsers = async (
  projectId: string
): Promise<IResponse> => {
  return await apiService.get(`/persona/persona-projects/${projectId}/allusers`);
};

/**
 * Add, update, or remove a collaborator on a Persona project.
 */
export const addPersonaProjectCollaborator = async (values: {
  collaborator_id: string;
  collaborator_name: string;
  collaborator_email: string;
  user_access_type: string;
  project_id: string;
  is_owner: boolean;
}): Promise<IResponse> => {
  return await apiService.post(
    `/persona/persona-projects/${values.project_id}/collaborator`,
    values
  );
};

/**
 * Transfer Persona project ownership to another Full Access collaborator.
 */
export const transferPersonaProjectOwner = async (values: {
  collaborator_id: string;
  collaborator_name: string;
  collaborator_email: string;
  project_id: string;
}): Promise<IResponse> => {
  return await apiService.post(
    `/persona/persona-projects/${values.project_id}/collaborator/owner`,
    values
  );
};

/**
 * Explicitly refresh report-derived Persona context for a project.
 */
export const refreshPersonaReportEnrichment = async (
  projectId: string
): Promise<IPersonaMarketContextStatus> => {
  const res = await apiService.post<{ data: IPersonaMarketContextStatus }>(
    `/persona/persona-projects/${projectId}/report-enrichment/refresh`,
    {}
  );
  return res.data;
};
